import React, { useState, useEffect, useRef } from "react";
import equal from "fast-deep-equal";

import { SongContext } from "./Song";
import Tone from "../lib/tone";
import buildSequencerStep, { SequencerStep } from "../lib/buildSequencerStep";
import { usePrevious } from "../lib/hooks";
import { MidiNote } from "../types/midi-notes";
import { Seconds } from "tone/build/esm/core/type/Units";

export const DEFAULT_NOTE_DURATION = 0.5;
export const DEFAULT_NOTE_SUBDIVISION = "4n";

export interface StepNoteType {
  name: MidiNote;
  duration?: number | string;
  velocity?: number;
}

export type StepType =
  | StepNoteType
  | StepNoteType[]
  | MidiNote
  | MidiNote[]
  | (StepNoteType | MidiNote)[]
  | null;

export interface TrackProps {
  id: number;
  steps?: StepType[];
  volume?: number;
  pan?: number;
  mute?: boolean;
  solo?: boolean;
  subdivision?: string;
  effects?: React.ReactNode[];
  children: React.ReactNode;
  onStepPlay?: (stepNotes: StepNoteType[], index: number) => void;
  handleTrackFinished: (id: number, trackFinished: boolean) => void;
}

export interface TrackConsumerProps extends TrackProps {
  isPlaying: boolean;
}

type TrackContextProps = {
  volume: number;
  pan: number;
  mute: boolean;
  solo: boolean;
  effectsChain?: any[];
  onInstrumentsUpdate?: (newInstruments: any[]) => void;
  onAddToEffectsChain?: (effect: any) => void;
  onRemoveFromEffectsChain?: (effect: any) => void;
};

export const TrackContext = React.createContext<TrackContextProps>({
  volume: 0,
  pan: 0,
  mute: false,
  solo: false,
  effectsChain: undefined,
  onInstrumentsUpdate: undefined,
  onAddToEffectsChain: undefined,
  onRemoveFromEffectsChain: undefined,
});

const TrackConsumer: React.FC<TrackConsumerProps> = ({
  // <Song /> props
  isPlaying,
  // <Track /> props
  id,
  steps = [],
  volume = 0,
  pan = 0,
  mute,
  solo,
  subdivision = DEFAULT_NOTE_SUBDIVISION,
  effects = [],
  children,
  onStepPlay,
  handleTrackFinished,
}) => {
  const [effectsChain, setEffectsChain] = useState<any[]>([]);
  const [instruments, setInstruments] = useState<any[]>([]);
  const sequencer = useRef<{
    start: Function;
    stop: Function;
    remove: Function;
    add: Function;
    dispose: Function;
    removeAll: Function;
  }>();
  const instrumentsRef = useRef<any>(instruments);

  useEffect(() => {
    instrumentsRef.current = instruments;
  }, [instruments]);

  /*
  Tone.Sequence can't easily play chords. By default, arrays within steps are flattened out and subdivided. However an array of notes is our preferred way of representing chords. To get around this, buildSequencerStep() will transform notes and put them in a notes field as an array. We can then loop through and run triggerAttackRelease() to play the note/s.
  */
  const sequencerSteps = steps.map(buildSequencerStep);
  const prevSequencerSteps: SequencerStep[] = usePrevious(sequencerSteps);
  const { exporting } = React.useContext(SongContext);

  useEffect(() => {
    // -------------------------------------------------------------------------
    // STEPS
    // -------------------------------------------------------------------------

    // Start/Stop sequencer!
    if (isPlaying || exporting) {
      sequencer.current = new Tone.Sequence(
        (_time: Seconds, step: SequencerStep) => {
          step.notes.forEach((note) => {
            instrumentsRef.current.forEach((instrument: any) => {
              instrument.triggerAttackRelease(
                note.name,
                note.duration || DEFAULT_NOTE_DURATION,
                undefined,
                note.velocity
              );
            });
          });

          if (typeof onStepPlay === "function") {
            onStepPlay(step.notes, step.index);
          }

          if (step.index + 1 === sequencerSteps.length) {
            handleTrackFinished(id, true);
          }
        },
        [...sequencerSteps],
        subdivision
      ).set({ loop: false });

      sequencer.current?.start(0);
    } else {
      if (sequencer.current) {
        sequencer.current.stop();
      }
    }
    /* eslint-disable-next-line */
  }, [isPlaying, exporting]);

  useEffect(() => {
    if (sequencer.current) {
      if (prevSequencerSteps?.length === sequencerSteps.length) {
        // When steps length is the same, update steps in a more efficient way
        sequencerSteps.forEach((step, i) => {
          const isEqual = equal(
            sequencerSteps[i].notes,
            prevSequencerSteps && prevSequencerSteps[i]
              ? prevSequencerSteps[i].notes
              : []
          );

          if (!isEqual) {
            if (sequencer.current?.remove) {
              sequencer.current?.remove(i);
              sequencer.current?.add && sequencer.current?.add(i, step);
            }
          }
        });
      } else {
        // When new steps are less or more then prev, remove all and add new steps
        sequencer.current.removeAll && sequencer.current.removeAll();
        sequencerSteps.forEach((step, i) => {
          sequencer.current?.add && sequencer.current?.add(i, step);
        });
      }
    }
    /* eslint-disable-next-line */
  }, [JSON.stringify(sequencerSteps)]);

  useEffect(() => {
    return function cleanup() {
      if (sequencer.current) {
        sequencer.current.dispose();
      }
    };
  }, []);

  const handleAddToEffectsChain = (effect: any) => {
    // console.log('<Track />', 'onAddToEffectsChain');

    setEffectsChain((prevEffectsChain: any[]) => {
      return [effect, ...prevEffectsChain];
    });
  };

  const handleRemoveFromEffectsChain = (effect: any) => {
    // console.log('<Track />', 'onRemoveFromEffectsChain', effect);

    setEffectsChain((prevEffectsChain) => {
      return prevEffectsChain.filter((e) => e.id !== effect.id);
    });
  };

  const handleInstrumentsUpdate = (newInstruments: any[]) => {
    setInstruments(newInstruments);
  };

  return (
    <TrackContext.Provider
      value={{
        effectsChain, // Used by Instrument
        pan,
        volume,
        mute: mute ?? false,
        solo: solo ?? false,
        onInstrumentsUpdate: handleInstrumentsUpdate,
        onAddToEffectsChain: handleAddToEffectsChain,
        onRemoveFromEffectsChain: handleRemoveFromEffectsChain,
      }}
    >
      {children}
      {effects}
    </TrackContext.Provider>
  );
};

const Track: React.FC<TrackProps> = (props) => {
  const { isPlaying } = React.useContext(SongContext);

  if (typeof window === "undefined") {
    return null;
  }

  return <TrackConsumer isPlaying={isPlaying} {...props} />;
};

export default Track;
